import type { Language } from '../../types/document'

// Home screen copy for the three kinds of paper. Machine-drafted outside Hindi and English.

export interface HomeCopy {
  heroTitle: string
  heroAccent: string
  heroSub: string
  govTitle: string
  govSub: string
  medTitle: string
  medSub: string
  dealTitle: string
  dealSub: string
  forOrgs: string
  forOrgsSub: string
  modesTitle: string
}

export const HOME_COPY: Record<Language, HomeCopy> = {
  en: {
    heroTitle: "Any paper.",
    heroAccent: "Explained.",
    heroSub: "Government letters, medical reports and agreements, in simple words, in your language.",
    govTitle: "Government letters",
    govSub: "Bills, notices, certificates",
    medTitle: "Medical reports",
    medSub: "Blood tests, lab reports",
    dealTitle: "Agreements",
    dealSub: "Rent, loan, insurance",
    forOrgs: "For organisations",
    forOrgsSub: "Help desks, CSCs, NGOs, hospitals",
    modesTitle: "What can Samjho read?",
  },
  hi: {
    heroTitle: "कोई भी कागज़।",
    heroAccent: "आसान भाषा में।",
    heroSub: "सरकारी चिट्ठियाँ, मेडिकल रिपोर्ट और समझौते — आसान शब्दों में, आपकी भाषा में।",
    govTitle: "सरकारी चिट्ठियाँ",
    govSub: "बिल, नोटिस, प्रमाण-पत्र",
    medTitle: "मेडिकल रिपोर्ट",
    medSub: "खून की जाँच, लैब रिपोर्ट",
    dealTitle: "समझौते",
    dealSub: "किराया, लोन, बीमा",
    forOrgs: "संस्थाओं के लिए",
    forOrgsSub: "हेल्प डेस्क, CSC, NGO, अस्पताल",
    modesTitle: "समझो क्या पढ़ सकता है?",
  },
  bn: {
    heroTitle: "যেকোনো কাগজ।",
    heroAccent: "সহজ ভাষায়।",
    heroSub: "সরকারি চিঠি, মেডিকেল রিপোর্ট আর চুক্তি — সহজ কথায়, আপনার ভাষায়।",
    govTitle: "সরকারি চিঠি",
    govSub: "বিল, নোটিস, শংসাপত্র",
    medTitle: "মেডিকেল রিপোর্ট",
    medSub: "রক্ত পরীক্ষা, ল্যাব রিপোর্ট",
    dealTitle: "চুক্তি",
    dealSub: "ভাড়া, ঋণ, বিমা",
    forOrgs: "প্রতিষ্ঠানের জন্য",
    forOrgsSub: "হেল্প ডেস্ক, CSC, NGO, হাসপাতাল",
    modesTitle: "সমঝো কী পড়তে পারে?",
  },
  mr: {
    heroTitle: "कोणताही कागद.",
    heroAccent: "सोप्या भाषेत.",
    heroSub: "सरकारी पत्रे, वैद्यकीय अहवाल आणि करार — सोप्या शब्दांत, तुमच्या भाषेत.",
    govTitle: "सरकारी पत्रे",
    govSub: "बिले, नोटिसा, दाखले",
    medTitle: "वैद्यकीय अहवाल",
    medSub: "रक्त तपासणी, लॅब अहवाल",
    dealTitle: "करार",
    dealSub: "भाडे, कर्ज, विमा",
    forOrgs: "संस्थांसाठी",
    forOrgsSub: "हेल्प डेस्क, CSC, NGO, रुग्णालये",
    modesTitle: "समझो काय वाचू शकते?",
  },
  ta: {
    heroTitle: "எந்தக் காகிதமும்.",
    heroAccent: "எளிமையாக.",
    heroSub: "அரசுக் கடிதங்கள், மருத்துவ அறிக்கைகள், ஒப்பந்தங்கள் — எளிய சொற்களில், உங்கள் மொழியில்.",
    govTitle: "அரசுக் கடிதங்கள்",
    govSub: "பில்கள், நோட்டீஸ்கள், சான்றிதழ்கள்",
    medTitle: "மருத்துவ அறிக்கைகள்",
    medSub: "இரத்தப் பரிசோதனை, லேப் அறிக்கை",
    dealTitle: "ஒப்பந்தங்கள்",
    dealSub: "வாடகை, கடன், காப்பீடு",
    forOrgs: "நிறுவனங்களுக்கு",
    forOrgsSub: "உதவி மையங்கள், CSC, NGO, மருத்துவமனைகள்",
    modesTitle: "சம்ஜோ எதைப் படிக்கும்?",
  },
  te: {
    heroTitle: "ఏ కాగితమైనా.",
    heroAccent: "సులభంగా.",
    heroSub: "ప్రభుత్వ లేఖలు, వైద్య నివేదికలు, ఒప్పందాలు — సులభమైన మాటల్లో, మీ భాషలో.",
    govTitle: "ప్రభుత్వ లేఖలు",
    govSub: "బిల్లులు, నోటీసులు, ధృవపత్రాలు",
    medTitle: "వైద్య నివేదికలు",
    medSub: "రక్త పరీక్షలు, ల్యాబ్ నివేదికలు",
    dealTitle: "ఒప్పందాలు",
    dealSub: "అద్దె, రుణం, బీమా",
    forOrgs: "సంస్థల కోసం",
    forOrgsSub: "హెల్ప్ డెస్క్‌లు, CSC, NGO, ఆసుపత్రులు",
    modesTitle: "సమ్ఝో ఏమి చదవగలదు?",
  },
  gu: {
    heroTitle: "કોઈપણ કાગળ.",
    heroAccent: "સરળ ભાષામાં.",
    heroSub: "સરકારી પત્રો, તબીબી રિપોર્ટ અને કરાર — સરળ શબ્દોમાં, તમારી ભાષામાં.",
    govTitle: "સરકારી પત્રો",
    govSub: "બિલ, નોટિસ, પ્રમાણપત્રો",
    medTitle: "તબીબી રિપોર્ટ",
    medSub: "લોહીની તપાસ, લૅબ રિપોર્ટ",
    dealTitle: "કરાર",
    dealSub: "ભાડું, લોન, વીમો",
    forOrgs: "સંસ્થાઓ માટે",
    forOrgsSub: "હેલ્પ ડેસ્ક, CSC, NGO, હોસ્પિટલો",
    modesTitle: "સમજો શું વાંચી શકે?",
  },
  kn: {
    heroTitle: "ಯಾವುದೇ ಕಾಗದ.",
    heroAccent: "ಸರಳವಾಗಿ.",
    heroSub: "ಸರ್ಕಾರಿ ಪತ್ರಗಳು, ವೈದ್ಯಕೀಯ ವರದಿಗಳು ಮತ್ತು ಒಪ್ಪಂದಗಳು — ಸರಳ ಪದಗಳಲ್ಲಿ, ನಿಮ್ಮ ಭಾಷೆಯಲ್ಲಿ.",
    govTitle: "ಸರ್ಕಾರಿ ಪತ್ರಗಳು",
    govSub: "ಬಿಲ್‌ಗಳು, ನೋಟಿಸ್‌ಗಳು, ಪ್ರಮಾಣಪತ್ರಗಳು",
    medTitle: "ವೈದ್ಯಕೀಯ ವರದಿಗಳು",
    medSub: "ರಕ್ತ ಪರೀಕ್ಷೆ, ಲ್ಯಾಬ್ ವರದಿ",
    dealTitle: "ಒಪ್ಪಂದಗಳು",
    dealSub: "ಬಾಡಿಗೆ, ಸಾಲ, ವಿಮೆ",
    forOrgs: "ಸಂಸ್ಥೆಗಳಿಗಾಗಿ",
    forOrgsSub: "ಸಹಾಯ ಕೇಂದ್ರಗಳು, CSC, NGO, ಆಸ್ಪತ್ರೆಗಳು",
    modesTitle: "ಸಮ್ಝೋ ಏನನ್ನು ಓದಬಲ್ಲದು?",
  },
  ml: {
    heroTitle: "ഏത് കടലാസും.",
    heroAccent: "ലളിതമായി.",
    heroSub: "സർക്കാർ കത്തുകൾ, മെഡിക്കൽ റിപ്പോർട്ടുകൾ, കരാറുകൾ — ലളിതമായ വാക്കുകളിൽ, നിങ്ങളുടെ ഭാഷയിൽ.",
    govTitle: "സർക്കാർ കത്തുകൾ",
    govSub: "ബില്ലുകൾ, നോട്ടീസുകൾ, സർട്ടിഫിക്കറ്റുകൾ",
    medTitle: "മെഡിക്കൽ റിപ്പോർട്ടുകൾ",
    medSub: "രക്തപരിശോധന, ലാബ് റിപ്പോർട്ട്",
    dealTitle: "കരാറുകൾ",
    dealSub: "വാടക, വായ്പ, ഇൻഷുറൻസ്",
    forOrgs: "സ്ഥാപനങ്ങൾക്ക്",
    forOrgsSub: "ഹെൽപ്പ് ഡെസ്കുകൾ, CSC, NGO, ആശുപത്രികൾ",
    modesTitle: "സമ്ഝോയ്ക്ക് എന്ത് വായിക്കാം?",
  },
  pa: {
    heroTitle: "ਕੋਈ ਵੀ ਕਾਗ਼ਜ਼।",
    heroAccent: "ਸੌਖੀ ਭਾਸ਼ਾ ਵਿੱਚ।",
    heroSub: "ਸਰਕਾਰੀ ਚਿੱਠੀਆਂ, ਮੈਡੀਕਲ ਰਿਪੋਰਟਾਂ ਅਤੇ ਇਕਰਾਰਨਾਮੇ — ਸੌਖੇ ਸ਼ਬਦਾਂ ਵਿੱਚ, ਤੁਹਾਡੀ ਭਾਸ਼ਾ ਵਿੱਚ।",
    govTitle: "ਸਰਕਾਰੀ ਚਿੱਠੀਆਂ",
    govSub: "ਬਿੱਲ, ਨੋਟਿਸ, ਸਰਟੀਫਿਕੇਟ",
    medTitle: "ਮੈਡੀਕਲ ਰਿਪੋਰਟਾਂ",
    medSub: "ਖ਼ੂਨ ਦੀ ਜਾਂਚ, ਲੈਬ ਰਿਪੋਰਟ",
    dealTitle: "ਇਕਰਾਰਨਾਮੇ",
    dealSub: "ਕਿਰਾਇਆ, ਕਰਜ਼ਾ, ਬੀਮਾ",
    forOrgs: "ਸੰਸਥਾਵਾਂ ਲਈ",
    forOrgsSub: "ਹੈਲਪ ਡੈਸਕ, CSC, NGO, ਹਸਪਤਾਲ",
    modesTitle: "ਸਮਝੋ ਕੀ ਪੜ੍ਹ ਸਕਦਾ ਹੈ?",
  },
  or: {
    heroTitle: "ଯେକୌଣସି କାଗଜ।",
    heroAccent: "ସହଜ ଭାଷାରେ।",
    heroSub: "ସରକାରୀ ଚିଠି, ଡାକ୍ତରୀ ରିପୋର୍ଟ ଓ ଚୁକ୍ତିନାମା — ସହଜ ଶବ୍ଦରେ, ଆପଣଙ୍କ ଭାଷାରେ।",
    govTitle: "ସରକାରୀ ଚିଠି",
    govSub: "ବିଲ୍, ନୋଟିସ୍, ପ୍ରମାଣପତ୍ର",
    medTitle: "ଡାକ୍ତରୀ ରିପୋର୍ଟ",
    medSub: "ରକ୍ତ ପରୀକ୍ଷା, ଲ୍ୟାବ୍ ରିପୋର୍ଟ",
    dealTitle: "ଚୁକ୍ତିନାମା",
    dealSub: "ଭଡ଼ା, ଋଣ, ବୀମା",
    forOrgs: "ସଂସ୍ଥା ପାଇଁ",
    forOrgsSub: "ହେଲ୍ପ ଡେସ୍କ, CSC, NGO, ଡାକ୍ତରଖାନା",
    modesTitle: "ସମଝୋ କ'ଣ ପଢ଼ିପାରେ?",
  },
  as: {
    heroTitle: "যিকোনো কাগজ।",
    heroAccent: "সহজ ভাষাত।",
    heroSub: "চৰকাৰী চিঠি, চিকিৎসা ৰিপৰ্ট আৰু চুক্তি — সহজ শব্দত, আপোনাৰ ভাষাত।",
    govTitle: "চৰকাৰী চিঠি",
    govSub: "বিল, জাননী, প্ৰমাণপত্ৰ",
    medTitle: "চিকিৎসা ৰিপৰ্ট",
    medSub: "তেজ পৰীক্ষা, লেব ৰিপৰ্ট",
    dealTitle: "চুক্তি",
    dealSub: "ভাড়া, ঋণ, বীমা",
    forOrgs: "প্ৰতিষ্ঠানৰ বাবে",
    forOrgsSub: "হেল্প ডেস্ক, CSC, NGO, চিকিৎসালয়",
    modesTitle: "সমঝোৱে কি পঢ়িব পাৰে?",
  },
}
